import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { CheckCircle, ArrowRight, Calendar, Hash, CreditCard, RefreshCw } from "lucide-react";

function formatDate(isoString) {
  if (!isoString) return "";
  return new Date(isoString).toLocaleDateString("nb-NO", {
    day: "numeric", month: "long", year: "numeric",
  });
}

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [subStatus, setSubStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const provider    = params.get("provider") || "vipps";
  const ref         = params.get("ref");
  const agreementId = params.get("agreementId");
  const isRecurring = provider === "vipps_recurring";

  useEffect(() => {
    const verify = async () => {
      try {
        if (isRecurring && agreementId) {
          // Confirm the recurring agreement with backend
          const confirmRes = await api.get(`/payment/vipps/subscribe/confirm?agreementId=${agreementId}`);
          setSubStatus(confirmRes.data.status);
        } else if (provider === "vipps" && ref) {
          await api.get(`/payment/vipps/callback?reference=${ref}`);
        }
        if (refresh) await refresh();

        // Fetch receipt
        const orderId = isRecurring ? agreementId : ref;
        if (orderId) {
          try {
            const res = await api.get(`/payment/receipt/${orderId}`);
            setOrder(res.data);
          } catch (_) {}
        }
      } catch (e) {
        // non-fatal — still show success screen
      } finally {
        setLoading(false);
      }
    };
    verify();
  }, [ref, agreementId, provider, isRecurring]);

  const paidAt     = order?.order?.paid_at || order?.order?.created_at;
  const expiresAt  = user?.subscribed_until;
  const autoRenews = isRecurring && subStatus === "ACTIVE";

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="max-w-lg mx-auto px-4 py-16">
        {loading ? (
          <div className="flex flex-col items-center gap-4 text-gray-500 py-24">
            <div className="w-8 h-8 border-2 border-fjord border-t-transparent rounded-full animate-spin" />
            <p>Bekrefter betaling…</p>
          </div>
        ) : (
          <>
            {/* Success header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-9 h-9 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1">Betaling mottatt!</h1>
              <p className="text-gray-500">
                {autoRenews
                  ? "Abonnementet ditt er aktivt. Det fornyes automatisk hver måned."
                  : "Full tilgang er nå aktivert på kontoen din i 30 dager."}
              </p>
            </div>

            {/* Receipt card */}
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm mb-6">
              <div className="bg-fjord text-white px-6 py-5">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-blue-200 uppercase tracking-widest mb-1">Kvittering</div>
                    <div className="text-xl font-bold">VeiKlar</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-blue-200">Beløp betalt</div>
                    <div className="text-2xl font-bold">79 kr</div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-5 space-y-4">
                <div className="flex items-start gap-3">
                  <Hash className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs text-gray-400 mb-0.5">Ordrenummer</div>
                    <div className="text-sm font-mono text-gray-800 break-all">
                      {isRecurring ? agreementId : ref || "—"}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Calendar className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs text-gray-400 mb-0.5">Betalingsdato</div>
                    <div className="text-sm text-gray-800">{formatDate(paidAt) || formatDate(new Date().toISOString())}</div>
                  </div>
                </div>

                {expiresAt && (
                  <div className="flex items-start gap-3">
                    <Calendar className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                    <div>
                      <div className="text-xs text-gray-400 mb-0.5">Tilgang gyldig til</div>
                      <div className="text-sm font-semibold text-green-700">{formatDate(expiresAt)}</div>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <RefreshCw className={`w-4 h-4 shrink-0 mt-0.5 ${autoRenews ? "text-fjord" : "text-gray-400"}`} />
                  <div>
                    <div className="text-xs text-gray-400 mb-0.5">Fornyelse</div>
                    <div className={`text-sm font-medium ${autoRenews ? "text-fjord" : "text-gray-600"}`}>
                      {autoRenews ? "Automatisk · 79 kr/måned · kanseller når som helst" : "Manuell · ingen automatisk trekk"}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CreditCard className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs text-gray-400 mb-0.5">Betalingsmetode</div>
                    <div className="text-sm text-gray-800">Vipps</div>
                  </div>
                </div>

                {user && (
                  <div className="border-t border-gray-100 pt-4">
                    <div className="text-xs text-gray-400 mb-0.5">Konto</div>
                    <div className="text-sm text-gray-800">{user.name || user.email || "—"}</div>
                    {user.email && user.name && (
                      <div className="text-xs text-gray-400">{user.email}</div>
                    )}
                  </div>
                )}

                <div className="border-t border-gray-100 pt-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">VeiKlar månedstilgang (30 dager)</span>
                    <span className="font-semibold text-gray-900">79,00 kr</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-400 mt-1">
                    <span>MVA (25 %)</span>
                    <span>inkludert</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-3 border-t border-gray-100">
                <p className="text-xs text-gray-400 text-center">
                  VeiKlar · support@veiklar.no · 14 dagers angrerett
                </p>
              </div>
            </div>

            {autoRenews && (
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-800 mb-4">
                <strong>Abonnementet fornyes automatisk.</strong> Du kan kansellere når som helst fra kontosiden — du beholder tilgangen til slutten av perioden.
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => navigate("/dashboard")}
                className="flex-1 bg-fjord text-white font-semibold rounded-xl py-3 hover:bg-fjord/90 transition-colors flex items-center justify-center gap-2"
              >
                Begynn å øve nå <ArrowRight className="w-4 h-4" />
              </button>
              {autoRenews && (
                <button
                  onClick={() => navigate("/account")}
                  className="px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-600 hover:border-gray-300 transition-colors"
                >
                  Konto
                </button>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
